import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { formatDate } from '../../utils/dates';
import { langFlags } from '../../common/langFlags';
import { timeAgo } from '../../utils/dates';
import CurrentDayHealth from '../../components/Common/CurrentDayHealth';
import { createNewReality, removeReality, testCloudFunction, createCoreSession, createCraftedSession } from '../../helpers/API/users';
import { useDispatch } from 'react-redux';
import { getUser } from '../../store/users/actions';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../helpers/firebase';

// Bind modal to your appElement (https://reactcommunity.org/react-modal/accessibility/)
Modal.setAppElement('#root'); // Adjust if your root element has a different ID

function capitalize(text) {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

const Realities = ({ user }) => {
  const dispatch = useDispatch();
  const [openReality, setOpenReality] = useState(user.realities.length === 1 ? 0 : null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    realityIndex: null,
    imageIndex: null,
  });
  const [duplicateLoading, setDuplicateLoading] = useState({});
  const [removeLoading, setRemoveLoading] = useState({});
  const [testLoading, setTestLoading] = useState(false);
  const [coreSessionLoading, setCoreSessionLoading] = useState({});
  const [craftedSessionLoading, setCraftedSessionLoading] = useState({});

  // Toggle the visibility of a reality's details
  const toggleReality = (index) => {
    setOpenReality(openReality === index ? null : index);
  };

  // Render icons based on the creator type
  const renderIconForCreatedBy = (type) => {
    if (type?.toLowerCase() === 'speaking') {
      return <i className="fas fa-microphone" style={{ color: '#007bff', marginLeft: '0.5rem' }} title="Speaking"></i>;
    }
    if (type?.toLowerCase() === 'writing') {
      return <i className="fas fa-pencil-alt" style={{ color: '#007bff', marginLeft: '0.5rem' }} title="Writing"></i>;
    }
    return null;
  };

  // Open the modal with the selected image
  const openImageModal = (realityIndex, imageIndex) => {
    setSelectedImage({ realityIndex, imageIndex });
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  // Close the modal
  const closeImageModal = () => {
    setSelectedImage({ realityIndex: null, imageIndex: null });
    setIsModalOpen(false);
    document.body.style.overflow = 'auto'; // Restore background scrolling
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && isModalOpen) {
      closeImageModal();
    }
    if (e.key === 'ArrowLeft' && isModalOpen) {
      navigatePrevImage();
    }
    if (e.key === 'ArrowRight' && isModalOpen) {
      navigateNextImage();
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, selectedImage]);

  // Navigate to the previous image within the same reality
  const navigatePrevImage = () => {
    const { realityIndex, imageIndex } = selectedImage;
    if (realityIndex === null || imageIndex === null) return;

    const reality = user.realities[realityIndex];
    if (!reality || !reality.images) return;

    if (imageIndex > 0) {
      setSelectedImage({ realityIndex, imageIndex: imageIndex - 1 });
    }
  };

  // Navigate to the next image within the same reality
  const navigateNextImage = () => {
    const { realityIndex, imageIndex } = selectedImage;
    if (realityIndex === null || imageIndex === null) return;

    const reality = user.realities[realityIndex];
    if (!reality || !reality.images) return;

    if (imageIndex < reality.images.length - 1) {
      setSelectedImage({ realityIndex, imageIndex: imageIndex + 1 });
    }
  };

  // Check if reality has incomplete first day (only 1 exercise instead of expected 2)
  const isRealityIncomplete = (reality) => {
    // If it's day 1 and current reality, check if it has incomplete exercises
    return reality.currentDay === 1;
  };

    // Duplicate reality function
  const handleDuplicateReality = async (reality) => {
    setDuplicateLoading(prev => ({ ...prev, [reality.id]: true }));

    try {
      // Copy ALL reality data to ensure nothing is missed
      const realityData = {
        title: reality.title,
        prompt1: reality.prompt1,
        prompt2: reality.prompt2,
        lang: reality.lang,
        voice: reality.voice,
        voiceDisplayName: reality.voiceDisplayName,
        createdBy: reality.createdBy,
        isPaid: reality.isPaid,
        isAdsEnabled: reality.isAdsEnabled,
        startDay: reality.startDay,
        daysByReality: reality.daysByReality || 30,
        hypnosis: reality.hypnosis, // Copy hypnosis data exactly as is
        // Copy any other fields that might exist
        ...(reality.bonus && { bonus: reality.bonus }),
        ...(reality.promoShownTimes !== undefined && { promoShownTimes: reality.promoShownTimes }),
        ...(reality.promoSelected !== undefined && { promoSelected: reality.promoSelected }),
        ...(reality.images && { images: reality.images }),
        ...(reality.status && { status: reality.status }),
        ...(reality.isClosed !== undefined && { isClosed: reality.isClosed }),
        ...(reality.showCreatedAnimation !== undefined && { showCreatedAnimation: reality.showCreatedAnimation }),
      };

      console.log('Duplicating reality with data:', realityData);

      const result = await createNewReality(user.id, realityData);

      // Update user's currentReality to the new reality ID
      if (result && result.realityId) {
        console.log('Setting new reality as current:', result.realityId);

        // Update currentReality field in Firebase
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          currentReality: result.realityId
        });

        console.log('Current reality updated successfully');
      }

      // Refresh user data to reflect the changes
      dispatch(getUser(user.id));

      alert('Reality duplicated successfully and set as current!');
    } catch (error) {
      console.error('Error duplicating reality:', error);
      alert('Failed to duplicate reality. Please try again.');
    } finally {
      setDuplicateLoading(prev => ({ ...prev, [reality.id]: false }));
    }
  };

    // Remove reality function
  const handleRemoveReality = async (realityId) => {
    if (!window.confirm('Are you sure you want to remove this reality? This action cannot be undone.')) {
      return;
    }

    setRemoveLoading(prev => ({ ...prev, [realityId]: true }));

    try {
      await removeReality(user.id, realityId);

      // Refresh user data
      dispatch(getUser(user.id));

      alert('Reality removed successfully!');
    } catch (error) {
      console.error('Error removing reality:', error);
      alert('Failed to remove reality. Please try again.');
    } finally {
      setRemoveLoading(prev => ({ ...prev, [realityId]: false }));
    }
  };

  // Test Cloud Function connection
  const handleTestCloudFunction = async () => {
    setTestLoading(true);

    try {
      await testCloudFunction();
      alert('Cloud Function test successful! Check console for details.');
    } catch (error) {
      console.error('Cloud Function test failed:', error);
      alert(`Cloud Function test failed: ${error.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  // Create Core Session
  const handleCreateCoreSession = async (realityId, dayId = null) => {
    const key = `${realityId}-${dayId || 'current'}`;
    setCoreSessionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const result = await createCoreSession(user.id, realityId, dayId);
      alert('Core session created successfully! Audio generation will start automatically.');
      
      // Refresh user data
      dispatch(getUser(user.id));
    } catch (error) {
      console.error('Error creating core session:', error);
      alert(`Failed to create core session: ${error.message}`);
    } finally {
      setCoreSessionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Create Crafted Session
  const handleCreateCraftedSession = async (realityId, dayId = null, forceRegenerate = false) => {
    const key = `${realityId}-${dayId || 'current'}`;
    setCraftedSessionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const result = await createCraftedSession(user.id, realityId, dayId, forceRegenerate);
      alert('Crafted session created successfully! Audio generation will start automatically.');
      
      // Refresh user data
      dispatch(getUser(user.id));
    } catch (error) {
      console.error('Error creating crafted session:', error);
      alert(`Failed to create crafted session: ${error.message}`);
    } finally {
      setCraftedSessionLoading(prev => ({ ...prev, [key]: false }));
    }
  };


  return (
    <div className="third-section">
      <h2>Realities</h2>

      {user.realities.map((reality, index) => {
        const isOpen = openReality === index;
        const isCurrentReality = reality.id === user.currentReality;

        return (
          <div key={reality.id} className={`reality-item ${isCurrentReality ? 'current-reality' : ''}`}>
            <div className="reality-header" onClick={() => toggleReality(index)}>
              <strong>{reality.title}</strong>
              <div className="reality-meta">
                <span>{reality.currentDay}</span>
                {reality.createdBy && (
                  <p className="reality-createdBy">{renderIconForCreatedBy(reality.createdBy)}</p>
                )}
                <span className={`bold ${reality.isPaid ? 'purple-badge' : 'green-badge'}`}>
                  {reality.isPaid ? 'PRO' : 'FREE'}
                </span>
                <span className={isCurrentReality ? 'green-badge' : 'red-badge'}>
                  {isCurrentReality ? 'Current' : 'Inactive'}
                </span>
              </div>
              <span className={`toggle-icon ${isOpen ? 'open' : ''}`}>
                <i className={`bx ${isOpen ? 'bx-chevron-down' : 'bx-chevron-right'}`}></i>
              </span>
            </div>
            {isOpen && (
              <div className="reality-details">
                {/* Top Row: 3 columns - Basic Info + Settings + Images */}
                <div className="reality-top-row">
                  {/* Column 1: Basic Info */}
                  <div className="reality-left-column">
                    <div className="reality-section">
                      <h4 className="section-title">Basic Information</h4>
                      <div className="compact-info">
                        <div className="info-item">
                          <strong>ID:</strong>
                          <span>{reality.id}</span>
                        </div>
                        <div className="info-item">
                          <strong>Created by:</strong>
                          <span>{capitalize(reality.createdBy)} {renderIconForCreatedBy(reality.createdBy)}</span>
                        </div>
                        <div className="info-item">
                          <strong>Current Day:</strong>
                          <span className="highlight">{reality.currentDay}</span>
                        </div>
                        <div className="info-item">
                          <strong>Created At:</strong>
                          <span>{formatDate(reality.createdAt, true)} - <span className="green-badge">{timeAgo(reality.createdAt)}</span></span>
                        </div>
                        <div className="info-item">
                          <strong>Last Updated:</strong>
                          <span>{formatDate(reality.updatedAt, true)} - <span className="green-badge">{timeAgo(reality.updatedAt)}</span></span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Column 2: Settings */}
                  <div className="reality-middle-column">
                    <div className="reality-section">
                      <h4 className="section-title">Settings</h4>
                      <div className="compact-info">
                        <div className="info-item">
                          <strong>Voice:</strong>
                          <span>{reality.voiceDisplayName}</span>
                        </div>
                        <div className="info-item">
                          <strong>Language:</strong>
                          <div className="language-value">
                            <img
                              src={langFlags[reality.lang]}
                              alt={reality.lang}
                              title={reality.lang}
                              className="flag"
                            />
                            <span>{reality.lang}</span>
                          </div>
                        </div>
                        <div className="info-item">
                          <strong>Plan:</strong>
                          <span 
                            className={`reality-status-badge ${reality.isPaid ? 'paid' : 'free'}`}
                          >
                            {reality.isPaid ? 'PRO' : 'FREE'}
                          </span>
                        </div>
                        <div className="info-item">
                          <strong>Reality #:</strong>
                          <span>{reality.realityNumber}</span>
                        </div>
                        <div className="info-item">
                          <strong>Start Day:</strong>
                          <span>{reality.startDay}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Column 3: Reality Images */}
                  <div className="reality-right-column">
                    <div className="reality-section">
                      <h4 className="section-title">Reality Images</h4>
                      {reality.images && reality.images.length > 0 ? (
                        <div className="images-gallery-compact">
                          {reality.images.map((imageUrl, idx) => (
                            <div
                              key={idx}
                              className="image-thumbnail-compact"
                              onClick={() => openImageModal(index, idx)}
                            >
                              <img 
                                src={imageUrl} 
                                alt={`Reality Image ${idx + 1}`}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.textContent = 'Access Denied';
                                  e.target.nextSibling.style.background = '#dc3545';
                                  e.target.nextSibling.style.color = 'white';
                                }}
                              />
                              <span className="image-label">Image {idx + 1}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-images">No images available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Full Width Prompts */}
                <div className="reality-prompts-section">
                  <div className="reality-section">
                    <h4 className="section-title">Prompts</h4>
                    <div className="prompts-container">
                      <div className="prompt-item">
                        <strong className="data-key">Prompt 1:</strong>
                        <p className="prompt-text">{reality.prompt1}</p>
                      </div>
                      <div className="prompt-item">
                        <strong className="data-key">Prompt 2:</strong>
                        <p className="prompt-text">{reality.prompt2}</p>
                      </div>
                    </div>
                  </div>
                </div>


                {/* Current Day Health Block */}
                <CurrentDayHealth
                  userId={user.id}
                  realityId={reality.id}
                  currentDay={reality.currentDay}
                  onCreateCoreSession={handleCreateCoreSession}
                  onCreateCraftedSession={handleCreateCraftedSession}
                  coreSessionLoading={coreSessionLoading}
                  craftedSessionLoading={craftedSessionLoading}
                />

                {/* Reality Actions */}
                <div className="reality-actions" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e9ecef' }}>
                  {/* Show duplicate button if reality is incomplete (day 1 with potentially missing exercises) */}
                  {isRealityIncomplete(reality) && (
                    <button
                      onClick={() => handleDuplicateReality(reality)}
                      disabled={duplicateLoading[reality.id]}
                      className="duplicate-reality-btn"
                      style={{ marginRight: '10px' }}
                    >
                      {duplicateLoading[reality.id] ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Duplicating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-copy"></i>
                          Duplicate Reality
                        </>
                      )}
                    </button>
                  )}

                  {/* Create Core Session button - only show on core session days */}
                  {[1, 11, 22, 33, 44, 55, 66, 77, 88, 99].includes(reality.currentDay) && (
                    <button
                      onClick={() => handleCreateCoreSession(reality.id)}
                      disabled={coreSessionLoading[`${reality.id}-current`]}
                      className="duplicate-reality-btn"
                      style={{ 
                        marginRight: '10px',
                        backgroundColor: '#28a745',
                        borderColor: '#28a745',
                        color: 'white'
                      }}
                    >
                      {coreSessionLoading[`${reality.id}-current`] ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-brain"></i>
                          Create Core Session
                        </>
                      )}
                    </button>
                  )}

                  {/* Remove reality button */}
                  <button
                    onClick={() => handleRemoveReality(reality.id)}
                    disabled={removeLoading[reality.id]}
                    className="remove-reality-btn"
                  >
                    {removeLoading[reality.id] ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Removing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash"></i>
                        Remove Reality
                      </>
                    )}
                  </button>
                </div>

              </div>
            )}
          </div>
        );
      })}

      {/* Full-Screen Image Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeImageModal}
        contentLabel="Reality Image"
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <button className="close-button" onClick={closeImageModal} aria-label="Close">&times;</button>
        {selectedImage.realityIndex !== null && selectedImage.imageIndex !== null && (
          <img
            src={user.realities[selectedImage.realityIndex].images[selectedImage.imageIndex]}
            alt={`Full Screen Reality Image ${selectedImage.imageIndex + 1}`}
            className="full-image"
          />
        )}
        {/* Navigation Buttons */}
        {selectedImage.realityIndex !== null && selectedImage.imageIndex > 0 && (
          <button
            className="nav-button left"
            onClick={() =>
              setSelectedImage((prev) => ({
                ...prev,
                imageIndex: prev.imageIndex - 1,
              }))
            }
            aria-label="Previous Image"
          >
            &#10094;
          </button>
        )}
        {selectedImage.realityIndex !== null &&
          selectedImage.imageIndex < user.realities[selectedImage.realityIndex].images.length - 1 && (
            <button
              className="nav-button right"
              onClick={() =>
                setSelectedImage((prev) => ({
                  ...prev,
                  imageIndex: prev.imageIndex + 1,
                }))
              }
              aria-label="Next Image"
            >
              &#10095;
            </button>
          )}
      </Modal>
    </div>
  );
};

export default Realities;
